import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo } from 'react'
import {
  CATEGORY_LABELS,
  getItemDef,
} from '../game/items'
import {
  getInitialBuildGate,
  HIDEOUT_TIER_META,
  meetsHideoutTierMinimum,
} from '../game/hideoutProgression'
import { ingredientOwned } from '../game/recipes'
import { WORKSTATION_LIST } from '../game/workstations'
import type {
  HideoutTierId,
  ItemStack,
  WorkstationId,
  WorkstationRuntimeState,
} from '../game/types'
import { useI18n } from '../i18n/useI18n'

type Props = {
  open: boolean
  onClose: () => void
  inventory: ItemStack[]
  hideoutTierId: HideoutTierId
  workstations: Record<WorkstationId, WorkstationRuntimeState>
  onBuild: (stationId: WorkstationId) => void
  vehicleQuestStarted: boolean
}

/** Stations that still need an initial build (have a defined build gate). */
export function listWorkstationsAwaitingInitialBuild(
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
  vehicleQuestStarted: boolean,
): typeof WORKSTATION_LIST {
  return WORKSTATION_LIST.filter((station) => {
    if (station.id === 'garage' && !vehicleQuestStarted) return false
    const ws = workstations[station.id]
    if (ws?.built) return false
    return getInitialBuildGate(station.id) != null
  })
}

/** Hub badge: how many initial builds remain and whether any can be afforded now (tier + costs). */
export function getPendingInitialBuildSummary(
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
  inventory: ItemStack[],
  hideoutTierId: HideoutTierId,
  vehicleQuestStarted: boolean,
): {
  pendingCount: number
  hasAffordableBuild: boolean
  affordableCount: number
} {
  const pending = listWorkstationsAwaitingInitialBuild(workstations, vehicleQuestStarted)
  let affordableCount = 0
  for (const station of pending) {
    const gate = getInitialBuildGate(station.id)
    if (!gate) continue
    if (!meetsHideoutTierMinimum(hideoutTierId, gate.minHideoutTier)) continue
    if (
      gate.costs.every((ing) => ingredientOwned(inventory, ing) >= ing.quantity)
    ) {
      affordableCount += 1
    }
  }
  return {
    pendingCount: pending.length,
    hasAffordableBuild: affordableCount > 0,
    affordableCount,
  }
}

export function BuildWorkstationsSheet({
  open,
  onClose,
  inventory,
  hideoutTierId,
  workstations,
  onBuild,
  vehicleQuestStarted,
}: Props) {
  const { t, pick } = useI18n()

  const pending = useMemo(
    () => listWorkstationsAwaitingInitialBuild(workstations, vehicleQuestStarted),
    [workstations, vehicleQuestStarted],
  )

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="build-workstations"
          className="fixed inset-0 z-56 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/70" />
          <motion.div
            className="relative max-h-[82vh] w-full max-w-[430px] overflow-y-auto rounded-t-2xl border-t border-stone-700 bg-stone-950 p-4 pb-6 shadow-2xl"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-stone-700" />
            <h2 className="text-base font-semibold text-stone-100">
              {t('hideout_build_modal_title')}
            </h2>
            <p className="mt-1 text-[11px] leading-snug text-stone-500">
              {t('hideout_build_modal_hint')}
            </p>

            {pending.length === 0 ? (
              <p className="mt-4 rounded-md border border-stone-800 bg-stone-800/60 p-3 text-sm text-stone-500">
                {t('hideout_build_modal_empty')}
              </p>
            ) : (
              <ul className="mt-4 space-y-3">
                {pending.map((station) => {
                  const gate = getInitialBuildGate(station.id)
                  if (!gate) return null
                  const tierOk = meetsHideoutTierMinimum(
                    hideoutTierId,
                    gate.minHideoutTier,
                  )
                  const costsOk = gate.costs.every(
                    (ing) => ingredientOwned(inventory, ing) >= ing.quantity,
                  )
                  const canBuild = tierOk && costsOk

                  return (
                    <li
                      key={station.id}
                      className="rounded-lg border border-stone-800 bg-stone-800/55 p-3"
                    >
                      <div className="flex gap-3">
                        <span className="text-2xl leading-none">{station.glyph}</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-stone-100">
                            {pick(station.name)}
                          </div>
                          <p className="mt-0.5 text-[11px] italic leading-snug text-stone-500">
                            {pick(gate.benefit)}
                          </p>
                        </div>
                      </div>

                      {!tierOk ? (
                        <p className="mt-2 text-[11px] text-amber-500/95">
                          {t('ws_detail_build_tier_gate', {
                            tier: pick(HIDEOUT_TIER_META[gate.minHideoutTier].name),
                          })}
                        </p>
                      ) : null}

                      <ul className="mt-2 space-y-1 border-t border-stone-800/90 pt-2">
                        {gate.costs.map((ing, i) => {
                          const own = ingredientOwned(inventory, ing)
                          const ok = own >= ing.quantity
                          const label =
                            'defId' in ing
                              ? pick(
                                  getItemDef(ing.defId)?.name ?? {
                                    nl: ing.defId,
                                    en: ing.defId,
                                  },
                                )
                              : pick(CATEGORY_LABELS[ing.category])
                          return (
                            <li
                              key={i}
                              className="flex justify-between gap-2 text-[11px] text-stone-400"
                            >
                              <span className="min-w-0 truncate">{label}</span>
                              <span
                                className={`shrink-0 font-mono tabular-nums ${
                                  ok ? 'text-emerald-400/90' : 'text-red-400/95'
                                }`}
                              >
                                {own} / {ing.quantity}
                              </span>
                            </li>
                          )
                        })}
                      </ul>

                      <button
                        type="button"
                        disabled={!canBuild}
                        onClick={() => onBuild(station.id)}
                        className="mt-3 w-full rounded-md border border-amber-800/60 bg-amber-900/40 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-amber-100 transition-colors hover:bg-amber-900/70 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {t('ws_detail_build_cta')}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}

            <button
              type="button"
              onClick={onClose}
              className="mt-4 w-full rounded-md border border-stone-800 bg-stone-800 px-3 py-2 text-sm uppercase tracking-wider text-stone-400 hover:bg-stone-800"
            >
              {t('assign_cancel')}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
